import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { uploadRecording } from '../services/recordingsService';
import {
  ChevronRight,
  Volume2,
  Mic,
  Square,
  Sparkles,
  RotateCcw,
  Timer,
  MessageCircle,
  ListChecks,
  Lightbulb,
  CheckCircle2,
  Send,
  Play,
} from 'lucide-react';

/* fr-FR speech */
const speak = (text, rate = 0.95) => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    u.lang = 'fr-FR';
    u.rate = rate;
    window.speechSynthesis.speak(u);
  } catch { /* silent */ }
};

/* -----------------------------------------------------------
   20 A1 themes — each with a 4-step Plan pour parler
   and optional Questions d'aide.
   Progression markers (per user spec):
     1–10  → présent
     11–15 → présent + passé composé simple
     16–20 → description + narration simple
----------------------------------------------------------- */
const THEMES_A1 = [
  {
    id: 't1', emoji: '👨‍👩‍👧', title: 'Ma famille', tense: 'présent',
    plan: ['Présente ta famille.', 'Décris les personnes.', 'Dis ce que vous faites ensemble.', 'Dis pourquoi tu aimes ta famille.'],
    helpers: ['Combien de personnes y a-t-il dans ta famille ?', 'Comment sont-elles ?', 'Que faites-vous ensemble ?'],
  },
  {
    id: 't2', emoji: '🏠', title: 'Ma maison', tense: 'présent',
    plan: ['Présente ta maison.', 'Décris quelques pièces.', 'Parle de ton endroit préféré.', 'Explique pourquoi tu l\'aimes.'],
    helpers: ['Où habites-tu ?', 'Combien y a-t-il de pièces ?', 'Quelle pièce préfères-tu ?'],
  },
  {
    id: 't3', emoji: '☀️', title: 'Ma routine du matin', tense: 'présent',
    plan: ['À quelle heure te lèves-tu ?', 'Que fais-tu le matin ?', 'Que manges-tu au petit-déjeuner ?', 'Comment te sens-tu le matin ?'],
    helpers: [],
  },
  {
    id: 't4', emoji: '🐶', title: 'Mon animal préféré', tense: 'présent',
    plan: ['Présente l\'animal.', 'Décris-le.', 'Explique ce qu\'il aime faire.', 'Dis pourquoi tu l\'aimes.'],
    helpers: [],
  },
  {
    id: 't5', emoji: '🍕', title: 'Mon repas préféré', tense: 'présent',
    plan: ['Présente ton repas préféré.', 'Décris les ingrédients.', 'Dis quand tu le manges.', 'Explique pourquoi tu l\'aimes.'],
    helpers: [],
  },
  {
    id: 't6', emoji: '👕', title: 'Mes vêtements préférés', tense: 'présent',
    plan: ['Présente les vêtements.', 'Décris les couleurs.', 'Dis quand tu les portes.', 'Explique pourquoi tu les aimes.'],
    helpers: [],
  },
  {
    id: 't7', emoji: '🎵', title: 'La musique que j\'aime', tense: 'présent',
    plan: ['Présente la musique.', 'Dis quand tu l\'écoutes.', 'Décris ce que tu ressens.', 'Explique pourquoi tu l\'aimes.'],
    helpers: [],
  },
  {
    id: 't8', emoji: '🏫', title: 'Mon école ou mon travail', tense: 'présent',
    plan: ['Présente ton école ou ton travail.', 'Décris une journée typique.', 'Parle des personnes que tu rencontres.', 'Donne ton opinion.'],
    helpers: [],
  },
  {
    id: 't9', emoji: '🎉', title: 'Mon anniversaire idéal', tense: 'présent',
    plan: ['Décris la fête.', 'Parle des invités.', 'Présente les activités.', 'Explique pourquoi cette fête est idéale.'],
    helpers: [],
  },
  {
    id: 't10', emoji: '🌍', title: 'Le pays que je veux visiter', tense: 'présent',
    plan: ['Présente le pays.', 'Parle des lieux à visiter.', 'Explique ce que tu veux découvrir.', 'Dis pourquoi ce pays t\'intéresse.'],
    helpers: [],
  },
  {
    id: 't11', emoji: '🛒', title: 'Une visite au supermarché', tense: 'présent + passé composé',
    plan: ['Présente le magasin.', 'Dis ce que tu as acheté.', 'Parle des prix.', 'Donne ton opinion.'],
    helpers: ['Où es-tu allé(e) ?', 'Qu\'as-tu acheté ?', 'Était-ce cher ou bon marché ?'],
  },
  {
    id: 't12', emoji: '☕', title: 'Une sortie au café', tense: 'présent + passé composé',
    plan: ['Présente le café.', 'Dis avec qui tu es allé(e).', 'Parle de ce que tu as commandé.', 'Décris ton expérience.'],
    helpers: ['Quand y es-tu allé(e) ?', 'Qu\'as-tu bu ?', 'As-tu aimé ce moment ?'],
  },
  {
    id: 't13', emoji: '🎬', title: 'Un film que tu as regardé', tense: 'présent + passé composé',
    plan: ['Présente le film.', 'Dis quand tu l\'as vu.', 'Décris l\'histoire simplement.', 'Donne ton avis.'],
    helpers: ['Quel film as-tu regardé ?', 'Était-il intéressant ?', 'Le recommandes-tu ?'],
  },
  {
    id: 't14', emoji: '🎁', title: 'Un cadeau que tu as reçu', tense: 'présent + passé composé',
    plan: ['Présente le cadeau.', 'Dis qui te l\'a offert.', 'Explique pourquoi tu l\'as aimé.', 'Dis ce que tu en fais aujourd\'hui.'],
    helpers: ['Quand l\'as-tu reçu ?', 'Pourquoi ce cadeau était-il spécial ?'],
  },
  {
    id: 't15', emoji: '🌳', title: 'Une journée au parc', tense: 'présent + passé composé',
    plan: ['Présente le parc.', 'Décris les activités.', 'Parle des personnes présentes.', 'Explique pourquoi tu as aimé cette journée.'],
    helpers: ['Qu\'as-tu fait ?', 'Avec qui étais-tu ?', 'Quel temps faisait-il ?'],
  },
  {
    id: 't16', emoji: '👧', title: 'Ton meilleur ami / ta meilleure amie', tense: 'description + narration',
    plan: ['Présente ton ami(e).', 'Décris son caractère.', 'Parle de vos activités.', 'Explique pourquoi cette personne est importante.'],
    helpers: ['Depuis quand êtes-vous amis ?', 'Que faites-vous ensemble ?'],
  },
  {
    id: 't17', emoji: '📅', title: 'Ton week-end dernier', tense: 'description + narration',
    plan: ['Présente ton week-end.', 'Décris les activités.', 'Parle des personnes rencontrées.', 'Donne ton opinion.'],
    helpers: ['Qu\'as-tu fait ?', 'Où es-tu allé(e) ?', 'As-tu passé un bon week-end ?'],
  },
  {
    id: 't18', emoji: '🎮', title: 'Une activité que tu aimes faire', tense: 'description + narration',
    plan: ['Présente l\'activité.', 'Explique quand tu la fais.', 'Dis avec qui tu la fais.', 'Explique pourquoi tu l\'aimes.'],
    helpers: ['Depuis quand fais-tu cette activité ?', 'Que ressens-tu quand tu la fais ?'],
  },
  {
    id: 't19', emoji: '🌦️', title: 'Une journée spéciale', tense: 'description + narration',
    plan: ['Présente la journée.', 'Explique ce qui s\'est passé.', 'Décris tes émotions.', 'Explique pourquoi tu t\'en souviens.'],
    helpers: ['Quand était-ce ?', 'Pourquoi cette journée était-elle spéciale ?'],
  },
  {
    id: 't20', emoji: '📸', title: 'Un souvenir heureux', tense: 'description + narration',
    plan: ['Présente le souvenir.', 'Dis où cela s\'est passé.', 'Décris les personnes présentes.', 'Explique pourquoi ce souvenir est important.'],
    helpers: ['Quand était-ce ?', 'Que s\'est-il passé ?', 'Pourquoi gardes-tu ce souvenir ?'],
  },
];

/* -----------------------------------------------------------
   20 A2 themes — structured oral presentations
   Progression :
     1–5   → présent + passé composé
     6–10  → présent + passé composé + imparfait
     11–15 → futur proche / futur simple / projets
     16–20 → comparaison / justification / opinion simple
----------------------------------------------------------- */
const THEMES_A2 = [
  {
    id: 'a2-t1', emoji: '📸', title: 'Un souvenir heureux', tense: 'présent + passé composé',
    plan: ['Présente le souvenir.', 'Raconte ce qui s\'est passé.', 'Décris tes émotions à ce moment-là.', 'Explique pourquoi ce souvenir compte pour toi.'],
    helpers: ['Quand était-ce ?', 'Avec qui étais-tu ?', 'Que ressens-tu en y repensant ?'],
  },
  {
    id: 'a2-t2', emoji: '🎁', title: 'Le plus beau cadeau que tu as reçu', tense: 'présent + passé composé',
    plan: ['Présente le cadeau.', 'Dis qui te l\'a offert et quand.', 'Décris pourquoi il est important.', 'Explique ce qu\'il représente aujourd\'hui.'],
    helpers: ['À quelle occasion l\'as-tu reçu ?', 'Qu\'est-ce qui le rend spécial ?'],
  },
  {
    id: 'a2-t3', emoji: '🎂', title: 'Un anniversaire mémorable', tense: 'présent + passé composé',
    plan: ['Présente la fête.', 'Raconte les moments forts.', 'Décris les personnes présentes.', 'Explique pourquoi tu t\'en souviens.'],
    helpers: ['Quel âge avais-tu ?', 'Qu\'avez-vous fait ce jour-là ?', 'Quel a été le meilleur moment ?'],
  },
  {
    id: 'a2-t4', emoji: '🎬', title: 'Un film qui t\'a marqué(e)', tense: 'présent + passé composé',
    plan: ['Présente le film.', 'Dis quand tu l\'as vu.', 'Résume l\'histoire simplement.', 'Donne ton avis personnel.'],
    helpers: ['Qui sont les personnages principaux ?', 'Pourquoi ce film t\'a touché(e) ?'],
  },
  {
    id: 'a2-t5', emoji: '☕', title: 'Une sortie agréable', tense: 'présent + passé composé',
    plan: ['Présente le lieu (café, restaurant ou parc).', 'Dis avec qui tu y es allé(e).', 'Raconte ce que vous avez fait.', 'Donne ton opinion sur ce moment.'],
    helpers: ['Quand était-ce ?', 'Qu\'as-tu particulièrement apprécié ?'],
  },
  {
    id: 'a2-t6', emoji: '🧒', title: 'Quand tu étais enfant', tense: 'imparfait',
    plan: ['Décris ton apparence à cette époque.', 'Parle de tes habitudes quotidiennes.', 'Présente tes activités préférées.', 'Explique ce que tu aimais le plus.'],
    helpers: ['Où vivais-tu ?', 'Avec qui jouais-tu ?', 'Qu\'aimais-tu faire le week-end ?'],
  },
  {
    id: 'a2-t7', emoji: '🏫', title: 'Ton école quand tu étais petit(e)', tense: 'imparfait',
    plan: ['Présente l\'école.', 'Décris une journée typique.', 'Parle de tes professeurs ou camarades.', 'Explique ce que tu aimais ou non.'],
    helpers: ['Où était l\'école ?', 'Comment étaient tes professeurs ?', 'Quel était ton souvenir préféré ?'],
  },
  {
    id: 'a2-t8', emoji: '🌧️', title: 'Une journée que tu n\'oublieras jamais', tense: 'passé composé + imparfait',
    plan: ['Plante le décor (quel temps, où, avec qui).', 'Raconte ce qui s\'est passé.', 'Décris tes émotions sur le moment.', 'Explique pourquoi cette journée reste unique.'],
    helpers: ['Quand cela s\'est-il passé ?', 'Pourquoi cette journée était-elle particulière ?'],
  },
  {
    id: 'a2-t9', emoji: '🏡', title: 'La maison où tu vivais avant', tense: 'imparfait',
    plan: ['Présente la maison.', 'Décris les pièces et l\'ambiance.', 'Parle de ce que tu y faisais.', 'Explique ce qui te manque.'],
    helpers: ['Où était-elle située ?', 'Quelle était ta pièce préférée ?'],
  },
  {
    id: 'a2-t10', emoji: '👦', title: 'Un ami d\'enfance', tense: 'imparfait + passé composé',
    plan: ['Présente cette personne.', 'Décris son caractère et ce que vous aimiez faire.', 'Raconte un souvenir particulier.', 'Explique pourquoi cette amitié comptait.'],
    helpers: ['Où vous êtes-vous rencontré(e)s ?', 'Qu\'aimiez-vous faire ensemble ?'],
  },
  {
    id: 'a2-t11', emoji: '✈️', title: 'Un voyage que tu aimerais faire', tense: 'futur proche',
    plan: ['Présente le pays ou la ville.', 'Dis quand tu vas y aller.', 'Explique ce que tu vas visiter.', 'Dis pourquoi ce voyage t\'attire.'],
    helpers: ['Avec qui vas-tu partir ?', 'Combien de temps vas-tu rester ?'],
  },
  {
    id: 'a2-t12', emoji: '💼', title: 'Ton métier idéal', tense: 'futur simple',
    plan: ['Présente le métier.', 'Explique pourquoi il te plaît.', 'Dis ce que tu feras au quotidien.', 'Explique comment tu y arriveras.'],
    helpers: ['Où travailleras-tu ?', 'Quelles études feras-tu ?'],
  },
  {
    id: 'a2-t13', emoji: '🏠', title: 'La maison de tes rêves', tense: 'futur',
    plan: ['Décris l\'extérieur de la maison.', 'Présente les pièces principales.', 'Explique l\'ambiance que tu créeras.', 'Dis avec qui tu y vivras.'],
    helpers: ['Sera-t-elle en ville ou à la campagne ?', 'Combien de pièces aura-t-elle ?'],
  },
  {
    id: 'a2-t14', emoji: '🌍', title: 'Un pays que tu visiteras un jour', tense: 'futur',
    plan: ['Présente le pays.', 'Dis ce que tu verras.', 'Explique ce que tu goûteras ou découvriras.', 'Dis pourquoi ce pays t\'attire.'],
    helpers: ['Quand espères-tu y aller ?', 'Que vas-tu rapporter de ce voyage ?'],
  },
  {
    id: 'a2-t15', emoji: '🎯', title: 'Tes objectifs pour les prochaines années', tense: 'futur',
    plan: ['Présente un objectif important.', 'Explique pourquoi il compte pour toi.', 'Dis ce que tu feras pour y arriver.', 'Imagine ta vie une fois cet objectif atteint.'],
    helpers: ['Dans combien de temps ?', 'Quelle sera la première étape ?'],
  },
  {
    id: 'a2-t16', emoji: '📱', title: 'Ton objet préféré', tense: 'description + opinion',
    plan: ['Présente l\'objet.', 'Décris-le précisément.', 'Explique à quoi il te sert.', 'Dis pourquoi il est important pour toi.'],
    helpers: ['Depuis quand l\'as-tu ?', 'Qu\'est-ce qui le rend irremplaçable ?'],
  },
  {
    id: 'a2-t17', emoji: '🎵', title: 'La musique dans ta vie', tense: 'opinion simple',
    plan: ['Présente le style de musique que tu aimes.', 'Dis quand tu l\'écoutes.', 'Explique l\'effet qu\'elle a sur toi.', 'Compare la musique à d\'autres activités relaxantes.'],
    helpers: ['Quel artiste écoutes-tu le plus ?', 'Pourquoi cette musique te parle ?'],
  },
  {
    id: 'a2-t18', emoji: '⚽', title: 'Un loisir qui te passionne', tense: 'description + justification',
    plan: ['Présente l\'activité.', 'Décris comment tu la pratiques.', 'Explique ses avantages.', 'Justifie pourquoi tu y consacres du temps.'],
    helpers: ['Depuis combien de temps en fais-tu ?', 'Qu\'est-ce que cela t\'apporte ?'],
  },
  {
    id: 'a2-t19', emoji: '👨‍👩‍👧', title: 'Une personne que tu admires', tense: 'opinion + justification',
    plan: ['Présente cette personne.', 'Décris ses qualités.', 'Raconte une chose qu\'elle a faite et qui t\'a marqué(e).', 'Explique pourquoi elle est un modèle pour toi.'],
    helpers: ['La connais-tu personnellement ?', 'Quelle qualité admires-tu le plus ?'],
  },
  {
    id: 'a2-t20', emoji: '✨', title: 'Un moment spécial aujourd\'hui', tense: 'expression personnelle libre',
    plan: ['Raconte ce moment.', 'Décris où tu étais et avec qui.', 'Exprime tes émotions précises.', 'Explique pourquoi ce moment compte aujourd\'hui.'],
    helpers: ['Qu\'as-tu ressenti exactement ?', 'Aimerais-tu revivre ce moment ?'],
  },
];

/* -----------------------------------------------------------
   20 B1 themes — structured 2-3 min oral presentations
   Generic 5-step framework (présenter / 2-3 idées /
   exemple / opinion / conclure) condensed into 4 plan cards.
   Progression :
     1–5   → passé composé + imparfait + narration développée
     6–10  → opinion + comparaison + cause/conséquence
     11–15 → futur + conditionnel + hypothèses simples
     16–20 → débat + argumentation + introduction au subjonctif
----------------------------------------------------------- */
const THEMES_B1 = [
  {
    id: 'b1-t1', emoji: '🌍', title: 'Un voyage qui a changé ta façon de voir le monde', tense: 'passé composé + imparfait',
    plan: ['Présente le voyage (où, quand, avec qui).', 'Développe deux moments forts qui t\'ont marqué(e).', 'Donne un exemple précis d\'une rencontre ou d\'un lieu.', 'Exprime ce que ce voyage t\'a appris et conclus.'],
    helpers: ['Qu\'est-ce qui a changé dans ta façon de penser ?', 'À qui recommanderais-tu cette expérience ?'],
  },
  {
    id: 'b1-t2', emoji: '📖', title: 'Un projet scolaire ou professionnel dont tu es fier(e)', tense: 'passé composé + imparfait',
    plan: ['Présente le projet et son contexte.', 'Décris deux ou trois difficultés rencontrées.', 'Donne un exemple précis de moment décisif.', 'Explique le résultat et ce que tu en retires.'],
    helpers: ['Étais-tu seul(e) ou en équipe ?', 'Que ferais-tu différemment aujourd\'hui ?'],
  },
  {
    id: 'b1-t3', emoji: '🎭', title: 'Une expérience inattendue', tense: 'passé composé + imparfait',
    plan: ['Plante le décor (où, quand, contexte).', 'Raconte deux moments clés de cette expérience.', 'Donne un détail précis qui montre la surprise.', 'Exprime tes émotions et ta conclusion.'],
    helpers: ['Pourquoi cet événement était-il inattendu ?', 'A-t-il changé quelque chose en toi ?'],
  },
  {
    id: 'b1-t4', emoji: '🏆', title: 'Un défi personnel que tu as réussi', tense: 'passé composé + imparfait',
    plan: ['Présente le défi.', 'Développe deux obstacles que tu as surmontés.', 'Donne un exemple concret d\'un moment difficile.', 'Explique ce que cette réussite t\'a appris.'],
    helpers: ['Comment es-tu resté(e) motivé(e) ?', 'Quel a été le moment le plus difficile ?'],
  },
  {
    id: 'b1-t5', emoji: '🤝', title: 'Une rencontre importante dans ta vie', tense: 'passé composé + imparfait',
    plan: ['Présente cette personne et le contexte de votre rencontre.', 'Développe deux qualités qui t\'ont marqué(e).', 'Raconte un moment partagé révélateur.', 'Explique son influence et conclus.'],
    helpers: ['Où vous êtes-vous rencontré(e)s ?', 'En quoi cette personne t\'a transformé(e) ?'],
  },
  {
    id: 'b1-t6', emoji: '🌱', title: 'Pourquoi faut-il protéger l\'environnement ?', tense: 'opinion + cause / conséquence',
    plan: ['Présente le problème environnemental.', 'Développe deux conséquences concrètes de l\'inaction.', 'Donne un exemple précis (canicules, biodiversité…).', 'Propose ta solution prioritaire et conclus.'],
    helpers: ['Quelle conséquence te préoccupe le plus ?', 'Qui doit agir en priorité ?'],
  },
  {
    id: 'b1-t7', emoji: '♻️', title: 'La consommation durable est-elle importante ?', tense: 'opinion + comparaison',
    plan: ['Présente la consommation durable.', 'Compare deux habitudes responsables (locale vs globale, neuf vs occasion).', 'Donne un exemple précis qui te concerne.', 'Exprime ton opinion personnelle et conclus.'],
    helpers: ['Quelles habitudes as-tu déjà adoptées ?', 'Quelle est la principale difficulté ?'],
  },
  {
    id: 'b1-t8', emoji: '🚲', title: 'Comment réduire la pollution dans les villes ?', tense: 'opinion + cause / conséquence',
    plan: ['Présente la pollution urbaine et ses causes.', 'Développe deux solutions concrètes (transports, urbanisme).', 'Donne un exemple précis d\'une ville inspirante.', 'Défends ta solution prioritaire et conclus.'],
    helpers: ['Quelle solution te semble la plus réaliste ?', 'Que peuvent faire les citoyens ?'],
  },
  {
    id: 'b1-t9', emoji: '🍎', title: 'Acheter local : bonne ou mauvaise idée ?', tense: 'opinion + comparaison',
    plan: ['Présente la consommation locale.', 'Compare deux avantages et deux limites.', 'Donne un exemple précis (un produit, un marché).', 'Donne ton opinion équilibrée et conclus.'],
    helpers: ['Le local est-il toujours plus écologique ?', 'Et le coût ?'],
  },
  {
    id: 'b1-t10', emoji: '🌍', title: 'Les gestes écologiques du quotidien', tense: 'opinion + cause / conséquence',
    plan: ['Présente l\'idée des petits gestes.', 'Développe deux gestes que tu considères efficaces.', 'Donne un exemple précis tiré de ta vie.', 'Hiérarchise les gestes et conclus.'],
    helpers: ['Quel geste est selon toi le plus utile ?', 'Les petits gestes suffisent-ils ?'],
  },
  {
    id: 'b1-t11', emoji: '✈️', title: 'Si tu pouvais vivre dans un autre pays…', tense: 'conditionnel',
    plan: ['Présente le pays que tu choisirais.', 'Développe deux raisons (culture, climat, style de vie).', 'Imagine concrètement ton quotidien là-bas.', 'Explique ce que tu regretterais le plus de chez toi.'],
    helpers: ['Pourquoi ce pays plutôt qu\'un autre ?', 'Y resterais-tu pour toujours ?'],
  },
  {
    id: 'b1-t12', emoji: '🏙️', title: 'Si tu étais maire de ta ville…', tense: 'conditionnel',
    plan: ['Présente ta ville en quelques mots.', 'Développe deux priorités que tu mettrais en place.', 'Donne un exemple précis d\'une mesure concrète.', 'Explique ce que tu attendrais des habitants.'],
    helpers: ['Quelle est la première décision que tu prendrais ?', 'Quels obstacles imagines-tu ?'],
  },
  {
    id: 'b1-t13', emoji: '🎓', title: 'Si tu pouvais changer l\'école…', tense: 'conditionnel',
    plan: ['Présente ce qui ne fonctionne pas selon toi.', 'Développe deux réformes que tu proposerais.', 'Donne un exemple précis d\'une bonne pratique vue ailleurs.', 'Exprime ton espoir pour l\'éducation et conclus.'],
    helpers: ['Quel souvenir scolaire t\'a marqué(e) ?', 'L\'école doit-elle préparer au travail ou à la vie ?'],
  },
  {
    id: 'b1-t14', emoji: '🤖', title: 'Comment imagines-tu la vie dans 20 ans ?', tense: 'futur',
    plan: ['Présente le contexte (technologie, climat, société).', 'Développe deux changements majeurs probables.', 'Donne un exemple précis (transport, travail, santé).', 'Exprime ton sentiment (espoir, inquiétude) et conclus.'],
    helpers: ['Qu\'est-ce qui t\'enthousiasme le plus ?', 'Qu\'est-ce qui t\'inquiète ?'],
  },
  {
    id: 'b1-t15', emoji: '💰', title: 'Que ferais-tu si tu gagnais un million d\'euros ?', tense: 'conditionnel',
    plan: ['Présente ta première réaction.', 'Développe deux usages que tu en ferais.', 'Donne un exemple précis (voyage, projet, don).', 'Explique ce que tu garderais pour plus tard et conclus.'],
    helpers: ['Changerais-tu de mode de vie ?', 'Aiderais-tu d\'autres personnes ?'],
  },
  {
    id: 'b1-t16', emoji: '🎉', title: 'Les festivals sont-ils importants pour la culture ?', tense: 'débat + argumentation',
    plan: ['Présente le rôle des festivals.', 'Développe deux apports culturels et sociaux.', 'Donne un exemple précis de festival que tu connais.', 'Anticipe une critique et défends ta position.'],
    helpers: ['Pour qui les festivals sont-ils essentiels ?', 'Y a-t-il des dérives à éviter ?'],
  },
  {
    id: 'b1-t17', emoji: '👥', title: 'Le bénévolat devrait-il être encouragé ?', tense: 'débat + argumentation',
    plan: ['Présente le bénévolat en quelques mots.', 'Développe deux bénéfices pour la société et pour soi.', 'Donne un exemple précis d\'engagement.', 'Reconnais une limite et conclus avec ta position.'],
    helpers: ['Qui devrait l\'encourager : l\'État, l\'école, les entreprises ?', 'Faut-il le rendre obligatoire ?'],
  },
  {
    id: 'b1-t18', emoji: '🏫', title: 'Il est important que les jeunes participent à des projets collectifs', tense: 'introduction au subjonctif',
    plan: ['Présente l\'idée des projets collectifs.', 'Développe deux raisons (compétences, ouverture aux autres).', 'Donne un exemple précis (associatif, sportif, artistique).', 'Conclus en disant ce que la société doit faire pour les y aider.'],
    helpers: ['Quels projets connais-tu autour de toi ?', 'Qu\'est-ce qui empêche les jeunes de s\'engager ?'],
  },
  {
    id: 'b1-t19', emoji: '🌍', title: 'Il faut que chacun fasse un effort pour protéger la planète', tense: 'introduction au subjonctif',
    plan: ['Présente l\'urgence environnementale.', 'Développe deux efforts individuels et collectifs.', 'Donne un exemple précis d\'engagement quotidien.', 'Réponds à ceux qui pensent que c\'est inutile et conclus.'],
    helpers: ['Les efforts individuels suffisent-ils ?', 'Quel rôle pour les gouvernements ?'],
  },
  {
    id: 'b1-t20', emoji: '🚇', title: 'Comment améliorer la qualité de vie dans les villes ?', tense: 'débat + argumentation',
    plan: ['Présente les problèmes urbains actuels.', 'Développe deux solutions structurelles (espaces verts, mobilité, logement).', 'Donne un exemple précis d\'une initiative inspirante.', 'Défends la solution qui te semble prioritaire et conclus.'],
    helpers: ['Qui doit financer ces transformations ?', 'Quelle ville prendrais-tu en modèle ?'],
  },
];

/* -----------------------------------------------------------
   20 B2 themes — 6-step plan each (structure conseillée B2):
     1) Introduire le sujet
     2) Présenter différents points de vue
     3) Développer des arguments
     4) Illustrer avec des exemples
     5) Exprimer une position personnelle
     6) Conclure avec une ouverture
   Progression markers:
     1–5   → argumentation structurée + nuance
     6–10  → subjonctif + cause/conséquence + analyse critique
     11–15 → conditionnel + hypothèses complexes + futur professionnel
     16–20 → futur antérieur + prospective + analyse sociétale
----------------------------------------------------------- */
const THEMES_B2 = [
  {
    id: 'b2-t1', emoji: '💼', title: 'Le télétravail représente-t-il l\'avenir du travail ?', tense: 'argumentation + nuance',
    plan: [
      'Introduis le sujet du télétravail et son essor récent.',
      'Présente les points de vue des employés, des entreprises et des syndicats.',
      'Développe deux arguments en faveur et deux arguments contre.',
      'Illustre avec un exemple précis (secteur, entreprise, étude).',
      'Exprime ta position personnelle de manière nuancée.',
      'Conclus avec une ouverture sur l\'évolution du monde du travail.',
    ],
    helpers: ['Le télétravail convient-il à tous les métiers ?', 'Quels sont les risques sociaux à long terme ?'],
  },
  {
    id: 'b2-t2', emoji: '🤖', title: 'L\'intelligence artificielle transformera-t-elle profondément les entreprises ?', tense: 'argumentation + comparaison',
    plan: [
      'Introduis le contexte de l\'IA dans le monde professionnel.',
      'Présente les points de vue optimistes et pessimistes.',
      'Développe deux arguments structurés (productivité vs emploi, créativité vs automatisation).',
      'Illustre avec un exemple concret d\'usage en entreprise.',
      'Exprime ton opinion personnelle nuancée.',
      'Conclus avec une ouverture sur le rôle humain face à l\'IA.',
    ],
    helpers: ['Quels métiers seront les plus transformés ?', 'L\'IA est-elle une menace ou un levier ?'],
  },
  {
    id: 'b2-t3', emoji: '🌍', title: 'La mondialisation est-elle toujours bénéfique ?', tense: 'argumentation + comparaison',
    plan: [
      'Introduis le concept de mondialisation et son histoire récente.',
      'Présente les points de vue économiques, sociaux et culturels.',
      'Développe deux arguments en faveur et deux limites majeures.',
      'Illustre avec un exemple précis (pays, secteur, événement).',
      'Exprime ta position en nuançant selon les domaines.',
      'Conclus avec une ouverture sur les évolutions possibles.',
    ],
    helpers: ['Qui sont les grands gagnants et les grands perdants ?', 'Peut-on parler de démondialisation ?'],
  },
  {
    id: 'b2-t4', emoji: '📈', title: 'L\'entrepreneuriat devrait-il être encouragé davantage ?', tense: 'argumentation + nuance',
    plan: [
      'Introduis le sujet de l\'entrepreneuriat aujourd\'hui.',
      'Présente les points de vue des jeunes, de l\'État et des grandes entreprises.',
      'Développe deux arguments pour et deux arguments en faveur d\'un encadrement.',
      'Illustre avec un exemple précis (start-up, success story, échec instructif).',
      'Exprime ta position en distinguant le rêve et la réalité.',
      'Conclus avec une ouverture sur le rôle de l\'éducation.',
    ],
    helpers: ['L\'entrepreneuriat est-il accessible à tous ?', 'Quels soutiens devraient être prioritaires ?'],
  },
  {
    id: 'b2-t5', emoji: '🏢', title: 'Comment attirer de nouvelles entreprises dans une ville ?', tense: 'argumentation + propositions',
    plan: [
      'Introduis l\'enjeu de l\'attractivité économique d\'une ville.',
      'Présente les attentes des entreprises, des habitants et des élus.',
      'Développe deux ou trois leviers (fiscalité, infrastructures, cadre de vie).',
      'Illustre avec un exemple précis d\'une ville qui a réussi.',
      'Exprime ta proposition prioritaire de manière argumentée.',
      'Conclus avec une ouverture sur les risques à éviter.',
    ],
    helpers: ['Comment éviter de défavoriser les habitants actuels ?', 'Quel rôle pour les universités et la culture ?'],
  },
  {
    id: 'b2-t6', emoji: '🚇', title: 'Il est essentiel que les villes investissent davantage dans les transports publics', tense: 'subjonctif',
    plan: [
      'Introduis la place des transports publics dans la qualité de vie urbaine.',
      'Présente les points de vue des usagers, des élus et des écologistes.',
      'Développe deux arguments structurés (environnement, équité sociale, économie).',
      'Illustre avec un exemple précis (métro, tramway, RER, ville modèle).',
      'Exprime ta position en utilisant le subjonctif (« il est essentiel que… »).',
      'Conclus avec une ouverture sur le financement et la coopération.',
    ],
    helpers: ['Comment convaincre les automobilistes ?', 'Faut-il rendre les transports gratuits ?'],
  },
  {
    id: 'b2-t7', emoji: '🌱', title: 'Bien que les citoyens soient plus sensibilisés à l\'écologie, les résultats restent limités', tense: 'subjonctif + concession',
    plan: [
      'Introduis le paradoxe entre prise de conscience et impact réel.',
      'Présente les points de vue des citoyens, des entreprises et des gouvernements.',
      'Développe deux causes structurelles (coût, incitations, lobbies, habitudes).',
      'Illustre avec un exemple précis (mode rapide, voiture individuelle, alimentation).',
      'Exprime ta position en utilisant la concession (« bien que… »).',
      'Conclus avec une ouverture sur les leviers prioritaires.',
    ],
    helpers: ['Le citoyen seul peut-il agir ?', 'Faut-il davantage de contraintes légales ?'],
  },
  {
    id: 'b2-t8', emoji: '🏙️', title: 'Les maires devraient-ils avoir davantage de pouvoir pour transformer leur ville ?', tense: 'argumentation + analyse critique',
    plan: [
      'Introduis le rôle des maires dans la transformation urbaine.',
      'Présente les points de vue centralisateurs et décentralisateurs.',
      'Développe deux arguments pour et deux arguments contre.',
      'Illustre avec un exemple précis (piétonnisation, logement, sécurité).',
      'Exprime ta position en distinguant les domaines d\'action.',
      'Conclus avec une ouverture sur la démocratie locale.',
    ],
    helpers: ['Plus de pouvoir = plus de responsabilité ?', 'Comment éviter les inégalités entre villes ?'],
  },
  {
    id: 'b2-t9', emoji: '💰', title: 'Les grandes entreprises ont-elles une responsabilité sociale particulière ?', tense: 'argumentation + analyse critique',
    plan: [
      'Introduis le concept de responsabilité sociale des entreprises (RSE).',
      'Présente les points de vue libéraux et interventionnistes.',
      'Développe deux arguments (impact économique, influence sociale, environnement).',
      'Illustre avec un exemple précis (engagement réel ou greenwashing).',
      'Exprime ta position en distinguant communication et action.',
      'Conclus avec une ouverture sur le rôle des consommateurs.',
    ],
    helpers: ['Peut-on faire confiance aux engagements volontaires ?', 'Faut-il des sanctions plus fortes ?'],
  },
  {
    id: 'b2-t10', emoji: '🌍', title: 'Comment concilier croissance économique et développement durable ?', tense: 'cause / conséquence + nuance',
    plan: [
      'Introduis la tension entre croissance et durabilité.',
      'Présente les points de vue des économistes, des écologistes et des entreprises.',
      'Développe deux pistes structurelles (croissance verte, sobriété, innovation).',
      'Illustre avec un exemple précis (secteur, pays, technologie).',
      'Exprime ta position en utilisant cause et conséquence.',
      'Conclus avec une ouverture sur le modèle de société souhaitable.',
    ],
    helpers: ['Faut-il choisir entre croissance et planète ?', 'Quel rôle pour la décroissance ?'],
  },
  {
    id: 'b2-t11', emoji: '📨', title: 'Tu écris au maire pour améliorer ta ville', tense: 'argumentation + propositions',
    plan: [
      'Introduis ta lettre en présentant le contexte et tes attentes.',
      'Présente quatre priorités : transports, sécurité, espaces verts, culture.',
      'Développe deux arguments pour ta proposition principale.',
      'Illustre avec un exemple précis (quartier, projet inspirant).',
      'Exprime ta position en hiérarchisant tes propositions.',
      'Conclus avec une ouverture sur la participation des habitants.',
    ],
    helpers: ['Quelle priorité défendrais-tu d\'abord ?', 'Comment associer les jeunes au projet ?'],
  },
  {
    id: 'b2-t12', emoji: '🏫', title: 'Comment préparer les jeunes au marché du travail de demain ?', tense: 'analyse + futur professionnel',
    plan: [
      'Introduis les transformations du marché du travail.',
      'Présente les points de vue de l\'école, des entreprises et des étudiants.',
      'Développe deux compétences essentielles (soft skills, numérique, langues, esprit critique).',
      'Illustre avec un exemple précis (formation, stage, métier émergent).',
      'Exprime ta position sur les réformes prioritaires.',
      'Conclus avec une ouverture sur l\'apprentissage tout au long de la vie.',
    ],
    helpers: ['L\'école prépare-t-elle vraiment au monde réel ?', 'Quel rôle pour les entreprises ?'],
  },
  {
    id: 'b2-t13', emoji: '💻', title: 'Si tu dirigeais une entreprise internationale…', tense: 'conditionnel',
    plan: [
      'Introduis le contexte de l\'entreprise imaginée (secteur, taille, marchés).',
      'Présente les points de vue des actionnaires, des employés et des clients.',
      'Développe deux priorités stratégiques (innovation, RSE, équité, expansion).',
      'Illustre avec un exemple précis de décision que tu prendrais.',
      'Exprime ta vision en utilisant le conditionnel.',
      'Conclus avec une ouverture sur ce que tu apprendrais de ce rôle.',
    ],
    helpers: ['Quelle culture d\'entreprise mettrais-tu en place ?', 'Comment équilibrer profit et éthique ?'],
  },
  {
    id: 'b2-t14', emoji: '🌎', title: 'Si les ressources naturelles devenaient plus rares…', tense: 'conditionnel + hypothèses complexes',
    plan: [
      'Introduis le scénario de raréfaction (eau, énergie, métaux, terres).',
      'Présente les points de vue des États, des entreprises et des citoyens.',
      'Développe deux types de réactions (régulation, innovation, sobriété, conflits).',
      'Illustre avec un exemple précis (pays exportateur, technologie, crise passée).',
      'Exprime ta position sur la réaction la plus juste.',
      'Conclus avec une ouverture sur la coopération internationale.',
    ],
    helpers: ['Comment éviter les inégalités d\'accès ?', 'Quel rôle pour la science ?'],
  },
  {
    id: 'b2-t15', emoji: '🚀', title: 'Si tu pouvais lancer une entreprise innovante…', tense: 'conditionnel + projection',
    plan: [
      'Introduis ton projet (secteur, mission, public visé).',
      'Présente les points de vue du marché, des investisseurs et des clients.',
      'Développe deux innovations clés et leur valeur ajoutée.',
      'Illustre avec un exemple précis d\'usage concret.',
      'Exprime ton impact potentiel sur la société.',
      'Conclus avec une ouverture sur les défis à anticiper.',
    ],
    helpers: ['Quel problème ton entreprise résoudrait-elle ?', 'Quelle serait ta plus grande difficulté ?'],
  },
  {
    id: 'b2-t16', emoji: '🔮', title: 'Comment imagines-tu l\'économie mondiale en 2050 ?', tense: 'futur simple + prospective',
    plan: [
      'Introduis les grandes tendances actuelles (climat, IA, démographie).',
      'Présente les points de vue optimistes et pessimistes.',
      'Développe deux évolutions majeures probables (commerce, énergie, travail).',
      'Illustre avec un exemple précis ou une projection chiffrée.',
      'Exprime ta vision personnelle en utilisant le futur simple.',
      'Conclus avec une ouverture sur ce qui dépend de nous.',
    ],
    helpers: ['Quel scénario est selon toi le plus probable ?', 'Qu\'est-ce qui pourrait tout changer ?'],
  },
  {
    id: 'b2-t17', emoji: '🤖', title: 'Lorsque l\'intelligence artificielle aura transformé la plupart des métiers…', tense: 'futur antérieur',
    plan: [
      'Introduis le scénario d\'une IA généralisée dans le travail.',
      'Présente les points de vue des employés, des employeurs et des chercheurs.',
      'Développe deux compétences humaines irremplaçables (créativité, empathie, jugement, sens).',
      'Illustre avec un exemple précis de métier transformé ou résistant.',
      'Exprime ta position en utilisant le futur antérieur.',
      'Conclus avec une ouverture sur la formation continue.',
    ],
    helpers: ['L\'humain restera-t-il indispensable ?', 'Comment former à ces compétences ?'],
  },
  {
    id: 'b2-t18', emoji: '🌍', title: 'Une fois que les grandes villes auront réduit leurs émissions de carbone…', tense: 'futur antérieur + prospective',
    plan: [
      'Introduis le contexte d\'une transition climatique aboutie.',
      'Présente les points de vue des urbanistes, des habitants et des entreprises.',
      'Développe deux nouveaux défis (qualité de l\'air, mobilité, justice sociale, biodiversité).',
      'Illustre avec un exemple précis de ville pionnière.',
      'Exprime ta position en utilisant le futur antérieur.',
      'Conclus avec une ouverture sur la prochaine étape urbaine.',
    ],
    helpers: ['Qui financera la transition ?', 'La ville durable est-elle attractive pour tous ?'],
  },
  {
    id: 'b2-t19', emoji: '🏢', title: 'Lorsque les entreprises auront adopté des modèles de travail plus flexibles…', tense: 'futur antérieur + analyse sociétale',
    plan: [
      'Introduis l\'évolution vers des modèles de travail flexibles.',
      'Présente les points de vue des salariés, des managers et des familles.',
      'Développe deux transformations sociales (équilibre vie-travail, lien social, productivité).',
      'Illustre avec un exemple précis (entreprise, secteur, pays).',
      'Exprime ta position en utilisant le futur antérieur.',
      'Conclus avec une ouverture sur la définition même du travail.',
    ],
    helpers: ['Le bureau a-t-il encore un avenir ?', 'Comment préserver le collectif ?'],
  },
  {
    id: 'b2-t20', emoji: '🌐', title: 'Quel sera selon toi le plus grand défi mondial des prochaines décennies ?', tense: 'prospective + réflexion stratégique',
    plan: [
      'Introduis le contexte mondial actuel et ses tensions.',
      'Présente plusieurs candidats au titre de « plus grand défi » (climat, IA, démographie, inégalités, géopolitique).',
      'Développe deux arguments pour défendre le défi que tu choisis.',
      'Illustre avec un exemple précis ou une donnée marquante.',
      'Exprime ta position en hiérarchisant les défis.',
      'Conclus avec une ouverture sur les leviers d\'action collective.',
    ],
    helpers: ['Quel acteur peut le mieux agir ?', 'Que peuvent faire les citoyens ?'],
  },
];

const THEMES_BY_LEVEL = { A1: THEMES_A1, A2: THEMES_A2, B1: THEMES_B1, B2: THEMES_B2 };
const LEVELS = ['A1', 'A2', 'B1', 'B2'];

const TIMER_MODES_BY_LEVEL = {
  A1: [
    { id: 'easy', name: 'Découverte', seconds: 30, sub: '30 sec' },
    { id: 'normal', name: 'Standard', seconds: 45, sub: '45 sec' },
    { id: 'challenge', name: 'Défi', seconds: 60, sub: '60 sec' },
  ],
  A2: [
    { id: 'standard', name: 'Standard', seconds: 60, sub: '60 sec' },
    { id: 'challenge', name: 'Challenge', seconds: 90, sub: '90 sec' },
    { id: 'expert', name: 'Expert', seconds: 120, sub: '120 sec' },
  ],
  B1: [
    { id: 'standard', name: 'Standard', seconds: 120, sub: '2 min' },
    { id: 'challenge', name: 'Challenge', seconds: 150, sub: '2 min 30' },
    { id: 'expert', name: 'Expert', seconds: 180, sub: '3 min' },
  ],
  B2: [
    { id: 'standard', name: 'Standard', seconds: 180, sub: '3 min' },
    { id: 'challenge', name: 'Challenge', seconds: 240, sub: '4 min' },
    { id: 'expert', name: 'Expert', seconds: 300, sub: '5 min' },
  ],
};

const PROGRESSION_BY_LEVEL = {
  A1: [
    { range: 'Thèmes 1–10', label: 'Présent' },
    { range: 'Thèmes 11–15', label: 'Présent + Passé composé simple' },
    { range: 'Thèmes 16–20', label: 'Description + narration simple' },
  ],
  A2: [
    { range: 'Thèmes 1–5', label: 'Présent + Passé composé' },
    { range: 'Thèmes 6–10', label: 'Présent + Passé composé + Imparfait' },
    { range: 'Thèmes 11–15', label: 'Futur proche / Futur simple / Projets' },
    { range: 'Thèmes 16–20', label: 'Comparaison / Justification / Opinion simple' },
  ],
  B1: [
    { range: 'Thèmes 1–5', label: 'Narration développée — passé composé + imparfait' },
    { range: 'Thèmes 6–10', label: 'Opinion structurée — cause, conséquence, comparaison' },
    { range: 'Thèmes 11–15', label: 'Hypothèses & projection — conditionnel et futur' },
    { range: 'Thèmes 16–20', label: 'Débat & argumentation — introduction au subjonctif' },
  ],
  B2: [
    { range: 'Thèmes 1–5', label: 'Argumentation structurée + comparaison de points de vue + nuance' },
    { range: 'Thèmes 6–10', label: 'Subjonctif + cause / conséquence + analyse critique' },
    { range: 'Thèmes 11–15', label: 'Conditionnel + hypothèses complexes + futur professionnel' },
    { range: 'Thèmes 16–20', label: 'Futur antérieur + prospective + réflexion stratégique + analyse sociétale' },
  ],
};

const FINAL_GOAL_BY_LEVEL = {
  A1: 'Objectif final : parler 45–60 secondes sur des sujets personnels en utilisant le présent et quelques phrases simples au passé.',
  A2: 'Objectif A2 : parler 1 à 2 minutes sur tes expériences, souvenirs, habitudes passées, projets futurs et goûts — avec une parole plus fluide et mieux organisée.',
  B1: 'Objectif B1 : tenir un discours structuré de 2 à 3 minutes sur des sujets concrets ou abstraits — raconter, justifier, comparer, hypothéquer et argumenter avec nuance.',
  B2: 'Objectif B2 : parler 3 à 5 minutes sur l\'économie, le monde professionnel, les politiques publiques, l\'innovation, l\'environnement, la mondialisation et les défis futurs — en développant une argumentation claire, structurée, nuancée et convaincante.',
};

const TEACHER_MESSAGE_BY_LEVEL = {
  A1: {
    intro: 'Votre réponse a été enregistrée avec succès. Votre professeur analysera votre production orale. Les commentaires personnalisés, les corrections et les conseils d\'amélioration seront disponibles dans votre tableau de bord.',
    closing: 'Continuez à parler régulièrement pour améliorer votre aisance en français.',
    bullets: null,
  },
  A2: {
    intro: 'Votre production orale a été enregistrée. Votre professeur analysera votre présentation.',
    closing: null,
    bullets: [
      'commentaires personnalisés',
      'conseils de prononciation',
      'corrections grammaticales',
      'suggestions de vocabulaire',
      'pistes d\'amélioration pour la prochaine présentation',
    ],
  },
  B1: {
    intro: 'Votre prise de parole a été transmise à votre professeur. Une analyse approfondie sera ajoutée à votre tableau de bord.',
    closing: 'Continuez à structurer vos idées avec des connecteurs et à varier vos temps : c\'est ainsi que naît une parole fluide et précise.',
    bullets: [
      'analyse de la structure argumentative',
      'précision des temps utilisés (passé composé, imparfait, conditionnel, subjonctif)',
      'richesse lexicale et nuances',
      'connecteurs logiques et fluidité',
      'pistes d\'amélioration pour gagner en aisance',
    ],
  },
  B2: {
    intro: 'Votre présentation a été enregistrée. Votre professeur analysera votre prise de parole en profondeur.',
    closing: 'Les commentaires détaillés seront disponibles dans votre tableau de bord.',
    bullets: [
      'la qualité de l\'argumentation',
      'la précision grammaticale',
      'la richesse du vocabulaire',
      'la cohérence du discours',
      'la maîtrise du registre formel',
      'la capacité à nuancer les idées',
    ],
  },
};

/* Difficult-word picker for the AI feedback recap */
const TRICKY_WORDS_BY_THEME = {
  default: ['restaurant', 'aujourd\'hui', 'mangeable', 'jardinage'],
  // A1
  t1: ['cousin', 'fils', 'maman', 'parents'],
  t2: ['cuisine', 'chambre', 'jardin'],
  t3: ['petit-déjeuner', 'douche'],
  t4: ['chien', 'oiseau', 'poisson'],
  t5: ['poulet', 'fromage', 'œuf'],
  t6: ['chaussures', 'écharpe', 'pull'],
  t7: ['chanson', 'rythme', 'mélodie'],
  t8: ['emploi du temps', 'collègue'],
  t9: ['anniversaire', 'gâteau', 'invités'],
  t10: ['voyage', 'aéroport', 'paysage'],
  t11: ['supermarché', 'caisse', 'fruits'],
  t12: ['cappuccino', 'serveur', 'terrasse'],
  t13: ['scène', 'personnage', 'réalisateur'],
  t14: ['cadeau', 'surprise'],
  t15: ['promenade', 'pique-nique'],
  t16: ['enfance', 'caractère'],
  t17: ['week-end', 'samedi', 'dimanche'],
  t18: ['passion', 'détente'],
  t19: ['émotion', 'mémorable'],
  t20: ['souvenir', 'enfance', 'photographie'],
  // A2
  'a2-t1': ['souvenir', 'émotion', 'mémoire'],
  'a2-t2': ['cadeau', 'précieux', 'inoubliable'],
  'a2-t3': ['anniversaire', 'gâteau', 'invités'],
  'a2-t4': ['cinéma', 'réalisateur', 'scénario'],
  'a2-t5': ['café', 'terrasse', 'restaurant'],
  'a2-t6': ['enfance', 'habitudes', 'quartier'],
  'a2-t7': ['école', 'professeur', 'récréation'],
  'a2-t8': ['journée', 'événement', 'inoubliable'],
  'a2-t9': ['maison', 'pièces', 'quartier'],
  'a2-t10': ['ami', 'enfance', 'jouions'],
  'a2-t11': ['voyage', 'aéroport', 'destination'],
  'a2-t12': ['métier', 'carrière', 'études'],
  'a2-t13': ['logement', 'spacieuse', 'lumineuse'],
  'a2-t14': ['voyage', 'culture', 'paysage'],
  'a2-t15': ['objectif', 'projet', 'avenir'],
  'a2-t16': ['objet', 'pratique', 'irremplaçable'],
  'a2-t17': ['musique', 'mélodie', 'rythme'],
  'a2-t18': ['loisir', 'passion', 'régulièrement'],
  'a2-t19': ['admiration', 'modèle', 'qualités'],
  'a2-t20': ['émotion', 'particulier', 'aujourd\'hui'],
  // B1
  'b1-t1': ['voyage', 'inoubliable', 'culturel', 'transformé'],
  'b1-t2': ['projet', 'équipe', 'persévérance', 'résultat'],
  'b1-t3': ['inattendu', 'imprévu', 'surprise', 'émotion'],
  'b1-t4': ['défi', 'obstacle', 'motivation', 'persévérance'],
  'b1-t5': ['rencontre', 'amitié', 'influence', 'parcours'],
  'b1-t6': ['environnement', 'biodiversité', 'conséquence', 'urgence'],
  'b1-t7': ['durable', 'responsable', 'consommation', 'occasion'],
  'b1-t8': ['pollution', 'transports', 'urbanisme', 'citoyens'],
  'b1-t9': ['local', 'producteur', 'circuit', 'écologique'],
  'b1-t10': ['quotidien', 'gestes', 'efficacité', 'engagement'],
  'b1-t11': ['ailleurs', 'culture', 'climat', 'regretter'],
  'b1-t12': ['mairie', 'priorité', 'mesure', 'habitants'],
  'b1-t13': ['école', 'réforme', 'apprentissage', 'pédagogie'],
  'b1-t14': ['technologie', 'avenir', 'transformation', 'inquiétude'],
  'b1-t15': ['million', 'fortune', 'générosité', 'investir'],
  'b1-t16': ['festival', 'culture', 'rassemblement', 'critique'],
  'b1-t17': ['bénévolat', 'engagement', 'société', 'obligatoire'],
  'b1-t18': ['collectif', 'participer', 'compétences', 'ouverture'],
  'b1-t19': ['planète', 'effort', 'individuel', 'gouvernement'],
  'b1-t20': ['urbain', 'mobilité', 'espaces verts', 'initiative'],
  // B2
  'b2-t1': ['télétravail', 'productivité', 'flexibilité', 'syndicats'],
  'b2-t2': ['intelligence artificielle', 'automatisation', 'compétitivité', 'éthique'],
  'b2-t3': ['mondialisation', 'délocalisation', 'inégalités', 'culturel'],
  'b2-t4': ['entrepreneuriat', 'écosystème', 'start-up', 'risque'],
  'b2-t5': ['attractivité', 'fiscalité', 'infrastructures', 'investisseurs'],
  'b2-t6': ['transports publics', 'urbanisme', 'subvention', 'équité'],
  'b2-t7': ['sensibilisation', 'concession', 'incitations', 'sobriété'],
  'b2-t8': ['décentralisation', 'compétences', 'gouvernance', 'démocratie locale'],
  'b2-t9': ['responsabilité sociale', 'greenwashing', 'gouvernance', 'engagement'],
  'b2-t10': ['croissance', 'durable', 'décroissance', 'transition'],
  'b2-t11': ['propositions', 'priorités', 'budget', 'participation citoyenne'],
  'b2-t12': ['compétences', 'employabilité', 'soft skills', 'reconversion'],
  'b2-t13': ['stratégie', 'leadership', 'expansion', 'culture d\'entreprise'],
  'b2-t14': ['raréfaction', 'coopération', 'régulation', 'géopolitique'],
  'b2-t15': ['innovation', 'disruption', 'impact', 'écosystème'],
  'b2-t16': ['économie mondiale', 'prospective', 'mutations', 'commerce'],
  'b2-t17': ['futur antérieur', 'irremplaçable', 'empathie', 'jugement'],
  'b2-t18': ['émissions', 'transition', 'biodiversité', 'mobilité douce'],
  'b2-t19': ['flexibilité', 'télétravail', 'collectif', 'équilibre'],
  'b2-t20': ['défi', 'enjeu', 'coopération', 'résilience'],
};

/* Simulated AI feedback dashboard */
function makeFeedback(themeId) {
  const base = 70 + Math.floor(Math.random() * 22);
  const pronun = Math.min(98, Math.max(65, base + (Math.floor(Math.random() * 8) - 4)));
  const fluid = Math.min(98, Math.max(60, base + (Math.floor(Math.random() * 10) - 5)));
  const vocab = Math.min(98, Math.max(60, base + (Math.floor(Math.random() * 10) - 5)));
  const clarity = Math.min(98, Math.max(60, base + (Math.floor(Math.random() * 10) - 5)));
  const allWords = TRICKY_WORDS_BY_THEME[themeId] || TRICKY_WORDS_BY_THEME.default;
  const tricky = allWords.slice(0, 2 + Math.floor(Math.random() * 2));
  return { pronun, fluid, vocab, clarity, tricky };
}

/* Recorder hook */
function useRecorder() {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
      return true;
    } catch {
      alert('Microphone non disponible — vérifie les permissions de ton navigateur.');
      return false;
    }
  }, []);

  const stop = useCallback(() => {
    if (mrRef.current && recording) {
      mrRef.current.stop();
      setRecording(false);
    }
  }, [recording]);

  const reset = useCallback(() => {
    setAudioUrl(null);
    setAudioBlob(null);
    chunksRef.current = [];
  }, []);

  return { recording, audioUrl, audioBlob, start, stop, reset };
}

/* -----------------------------------------------------------
   Theme detail (5-step flow)
----------------------------------------------------------- */
function ThemeDetail({ theme, timerSeconds, level = 'A1' }) {
  const { recording, audioUrl, audioBlob, start, stop, reset } = useRecorder();
  const [step, setStep] = useState(1); // 1..5
  const [remaining, setRemaining] = useState(timerSeconds);
  const [phase, setPhase] = useState('idle');
  const [feedback, setFeedback] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const tickRef = useRef(null);

  // Reset whenever theme or timer changes
  useEffect(() => {
    setStep(1);
    setRemaining(timerSeconds);
    setPhase('idle');
    setFeedback(null);
    setSubmitted(false);
    setSubmitError('');
    reset();
    if (tickRef.current) clearInterval(tickRef.current);
    if (recording) stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, timerSeconds]);

  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  const handleStart = async () => {
    setFeedback(null);
    reset();
    setRemaining(timerSeconds);
    const ok = await start();
    if (!ok) return;
    setPhase('recording');
    setStep(4);
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(tickRef.current);
          stop();
          setPhase('done');
          setFeedback(makeFeedback(theme.id));
          setStep(5);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  };

  const handleStopEarly = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    stop();
    setPhase('done');
    setFeedback(makeFeedback(theme.id));
    setStep(5);
  };

  const handleReset = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    setStep(3);
    setRemaining(timerSeconds);
    setPhase('idle');
    setFeedback(null);
    setSubmitted(false);
    setSubmitError('');
    reset();
  };

  const handleSubmit = async () => {
    if (!audioBlob) {
      setSubmitError("Aucun enregistrement à envoyer.");
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await uploadRecording(audioBlob, {
        level,
        exerciseId: theme.id || `${level}-guidee`.toLowerCase(),
        exerciseTitle: `${theme.title || theme.name || 'Expression orale guidée'} — Expression orale guidée`,
        promptId: String(theme.id || ''),
        promptText: theme.topic || theme.title || '',
        durationSeconds: timerSeconds - remaining,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Envoi impossible. Réessaie dans un instant.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((timerSeconds - remaining) / timerSeconds) * 100;

  const Step = ({ n, title, icon: Icon, active, complete, children }) => (
    <div
      className={`rounded-2xl border transition-colors ${
        active ? 'bg-white border-[#D4AF37] shadow-sm' : complete ? 'bg-white border-[#E8E4DC]' : 'bg-[#FAF7F0]/60 border-[#E8E4DC]'
      }`}
      data-testid={`step-${n}`}
    >
      <div className="flex items-start gap-3 px-5 py-4">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
            complete
              ? 'bg-emerald-100 text-emerald-700'
              : active
              ? 'bg-[#1A2F4A] text-[#D4AF37]'
              : 'bg-white border border-[#E8E4DC] text-[#6B4E37]'
          }`}
        >
          {complete ? <CheckCircle2 className="w-4 h-4" /> : n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icon className="w-3.5 h-3.5 text-[#D4AF37]" />
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold">Étape {n}</p>
          </div>
          <h3
            className="text-base font-bold text-[#1A2F4A] leading-tight mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {title}
          </h3>
          <div className="text-[13px] text-[#1A2F4A]/90 leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid={`theme-detail-${theme.id}`}>
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1A2F4A] to-[#243a58] rounded-2xl px-6 py-5 text-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-1.5">Thème · {level}</p>
            <h2
              className="text-2xl font-bold flex items-center gap-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="theme-title"
            >
              <span className="text-3xl" aria-hidden="true">{theme.emoji}</span>
              {theme.title}
            </h2>
            <p className="text-[11px] text-white/70 italic mt-1" data-testid="theme-tense">
              Temps visé : {theme.tense}
            </p>
          </div>
          <button
            onClick={() => speak(theme.title)}
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-white text-[#1A2F4A] rounded-full px-4 py-2 text-xs font-bold transition-colors flex-shrink-0"
            data-testid="theme-listen"
          >
            <Volume2 className="w-3.5 h-3.5" /> Écouter
          </button>
        </div>
      </div>

      {/* Étape 1 — Lire le sujet */}
      <Step
        n={1}
        title="Lire le sujet"
        icon={MessageCircle}
        active={step === 1}
        complete={step > 1}
      >
        <p>
          Découvre ton thème : <strong>{theme.title}</strong>.
        </p>
        {step === 1 && (
          <button
            onClick={() => setStep(2)}
            className="mt-3 inline-flex items-center gap-2 bg-[#1A2F4A] text-white rounded-full px-4 py-1.5 text-[11px] font-bold hover:bg-[#243a58] transition-colors"
            data-testid="step-1-continue"
          >
            J'ai compris <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </Step>

      {/* Étape 2 — Suivre le plan */}
      <Step
        n={2}
        title="Suivre le plan"
        icon={ListChecks}
        active={step === 2}
        complete={step > 2}
      >
        <ol className="space-y-1.5 list-none" data-testid="theme-plan">
          {theme.plan.map((p, i) => (
            <li key={i} className="flex items-start gap-2.5" data-testid={`plan-step-${i}`}>
              <span className="w-5 h-5 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/30 text-[10px] font-bold text-[#1A2F4A] flex items-center justify-center flex-shrink-0 mt-0.5">
                {i + 1}
              </span>
              <span>{p}</span>
              <button
                onClick={() => speak(p)}
                className="text-[#D4AF37] hover:text-[#1A2F4A] transition-colors flex-shrink-0 mt-1"
                aria-label={`Écouter l'étape ${i + 1}`}
                data-testid={`plan-listen-${i}`}
              >
                <Volume2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ol>
        {step === 2 && (
          <button
            onClick={() => setStep(3)}
            className="mt-3 inline-flex items-center gap-2 bg-[#1A2F4A] text-white rounded-full px-4 py-1.5 text-[11px] font-bold hover:bg-[#243a58] transition-colors"
            data-testid="step-2-continue"
          >
            J'ai mon plan <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </Step>

      {/* Étape 3 — Préparer ses idées */}
      <Step
        n={3}
        title="Préparer ses idées"
        icon={Lightbulb}
        active={step === 3}
        complete={step > 3}
      >
        {theme.helpers.length > 0 ? (
          <>
            <p className="text-[12px] text-[#6B4E37] italic mb-2">Questions d'aide :</p>
            <ul className="space-y-1.5 list-none" data-testid="theme-helpers">
              {theme.helpers.map((q, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`helper-${i}`}>
                  <span className="text-[#D4AF37] mt-0.5 flex-shrink-0" aria-hidden="true">•</span>
                  <span className="italic">{q}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="italic text-[#6B4E37]">
            Prends une grande respiration. Pense à 3-4 idées que tu peux dire avec des phrases simples.
          </p>
        )}
        {step === 3 && (
          <button
            onClick={() => setStep(4)}
            className="mt-3 inline-flex items-center gap-2 bg-[#1A2F4A] text-white rounded-full px-4 py-1.5 text-[11px] font-bold hover:bg-[#243a58] transition-colors"
            data-testid="step-3-continue"
          >
            Je suis prêt(e) <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </Step>

      {/* Étape 4 — Enregistrer sa réponse */}
      <Step
        n={4}
        title="Enregistrer sa réponse"
        icon={Mic}
        active={step === 4}
        complete={step > 4}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs">
            <div className="inline-flex items-center gap-1.5 text-[#6B4E37]">
              <Timer className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span>{phase === 'recording' ? `${remaining}s restantes` : `${timerSeconds}s pour parler`}</span>
            </div>
            {phase === 'recording' && <span className="text-[#D4AF37] font-bold animate-pulse">● REC</span>}
          </div>
          <div className="w-full h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden" data-testid="theme-timer-bar">
            <div className="h-full rounded-full bg-[#D4AF37] transition-all duration-700" style={{ width: `${progress}%` }} />
          </div>

          {phase === 'idle' && step >= 4 && (
            <button
              onClick={handleStart}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full py-3 text-sm font-bold transition-colors"
              data-testid="step-4-start"
            >
              <Mic className="w-4 h-4" /> Démarrer & m'enregistrer
            </button>
          )}
          {phase === 'recording' && (
            <button
              onClick={handleStopEarly}
              className="w-full inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white rounded-full py-3 text-sm font-bold animate-pulse"
              data-testid="step-4-stop"
            >
              <Square className="w-4 h-4" /> Arrêter
            </button>
          )}
          {phase === 'done' && (
            <button
              onClick={handleReset}
              className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-[#1A2F4A] text-[#1A2F4A] hover:bg-[#FAF7F0] rounded-full py-3 text-sm font-bold transition-colors"
              data-testid="step-4-retry"
            >
              <RotateCcw className="w-4 h-4" /> Réessayer
            </button>
          )}

          {audioUrl && <audio controls src={audioUrl} className="w-full h-9" data-testid="theme-playback" />}
        </div>
      </Step>

      {/* Étape 5 — Recevoir un retour */}
      <Step
        n={5}
        title="Recevoir un retour"
        icon={Sparkles}
        active={step === 5}
        complete={false}
      >
        {!feedback ? (
          <p className="italic text-[#6B4E37]">
            Termine ton enregistrement pour recevoir un retour automatique sur ta prononciation, ta fluidité et ton vocabulaire.
          </p>
        ) : (
          <div className="space-y-3" data-testid="feedback-panel">
            <div className="bg-[#1A2F4A] rounded-2xl p-4 text-white">
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold mb-3 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Retour automatique
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Prononciation', value: feedback.pronun },
                  { label: 'Fluidité', value: feedback.fluid },
                  { label: 'Vocabulaire', value: feedback.vocab },
                  { label: 'Clarté', value: feedback.clarity },
                ].map((m) => (
                  <div key={m.label}>
                    <div className="flex justify-between text-[10px] text-white/70 mb-1 font-medium">
                      <span>{m.label}</span>
                      <span className="text-[#D4AF37] font-bold">{m.value}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-[#D4AF37] transition-all duration-700" style={{ width: `${m.value}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold mb-1">Mots à retravailler</p>
                <div className="flex flex-wrap gap-1.5" data-testid="tricky-words">
                  {feedback.tricky.map((w) => (
                    <span key={w} className="text-[11px] bg-white/10 text-white rounded-full px-2.5 py-0.5">{w}</span>
                  ))}
                </div>
              </div>
              <div className="mt-3 text-[11px] text-white/70 italic">Temps de parole : {timerSeconds - remaining}s / {timerSeconds}s.</div>
            </div>

            {/* Send to teacher */}
            {!submitted ? (
              <>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !audioBlob}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#1A2F4A] text-[#1A2F4A] hover:text-[#D4AF37] rounded-full py-2.5 text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-to-teacher"
                >
                  <Send className="w-3.5 h-3.5" />
                  {submitting ? 'Envoi en cours…' : 'Envoyer à mon professeur'}
                </button>
                {submitError && (
                  <p className="text-[11px] text-red-600 mt-1.5" data-testid="submit-error">{submitError}</p>
                )}
              </>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="teacher-confirmation">
                <p className="text-[11px] text-emerald-700 uppercase tracking-wider font-bold mb-1.5">📋 Enregistrement envoyé</p>
                <p className="text-[12px] text-[#1A2F4A] leading-relaxed">
                  {TEACHER_MESSAGE_BY_LEVEL[level].intro}
                </p>
                {TEACHER_MESSAGE_BY_LEVEL[level].bullets && (
                  <>
                    <p className="text-[11px] text-[#6B4E37] uppercase tracking-wider font-bold mt-2.5 mb-1">
                      Vous recevrez dans votre tableau de bord :
                    </p>
                    <ul className="space-y-1 list-none">
                      {TEACHER_MESSAGE_BY_LEVEL[level].bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-[12px] text-[#1A2F4A]" data-testid={`teacher-bullet-${i}`}>
                          <span className="text-[#D4AF37] mt-0.5 flex-shrink-0">•</span>
                          {b}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {TEACHER_MESSAGE_BY_LEVEL[level].closing && (
                  <p className="text-[12px] text-[#6B4E37] italic mt-2">
                    {TEACHER_MESSAGE_BY_LEVEL[level].closing}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </Step>
    </div>
  );
}

/* ===========================================================
   MAIN PAGE
=========================================================== */
export default function ExpressionOraleGuidee() {
  const [level, setLevel] = useState('A1');
  const themes = THEMES_BY_LEVEL[level];
  const timerModes = TIMER_MODES_BY_LEVEL[level];

  const [activeId, setActiveId] = useState(themes[0].id);
  const [mode, setMode] = useState(timerModes[1].id);

  // Reset active theme + timer mode when level changes
  useEffect(() => {
    const next = THEMES_BY_LEVEL[level];
    const nextModes = TIMER_MODES_BY_LEVEL[level];
    setActiveId(next[0].id);
    setMode(nextModes[1].id);
  }, [level]);

  const active = themes.find((t) => t.id === activeId) || themes[0];
  const timerSeconds = (timerModes.find((t) => t.id === mode) || timerModes[1]).seconds;

  const goToTheme = (id) => {
    setActiveId(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* Header */}
      <section className="pt-12 pb-6">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="flex items-center gap-1.5 text-[10px] text-[#999] mb-6 flex-wrap" data-testid="breadcrumb">
            <Link to="/speaking-lab" className="hover:text-[#D4AF37] transition-colors">Speaking Lab</Link>
            <ChevronRight className="w-3 h-3" />
            <span>S'exprimer</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#1A2F4A] font-bold">Expression orale guidée par thème</span>
          </div>

          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-10 bg-[#D4AF37]/50" />
              <ListChecks className="w-5 h-5 text-[#D4AF37]" />
              <div className="h-px w-10 bg-[#D4AF37]/50" />
            </div>
            <h1
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="page-title"
            >
              Expression orale guidée par thème
            </h1>
            <p className="text-sm text-[#6B4E37] italic max-w-md mx-auto leading-relaxed">
              {{
                A1: '20 thèmes A1 — pour apprendre à structurer une courte présentation en 5 étapes : lire, planifier, préparer, enregistrer, recevoir un retour.',
                A2: '20 thèmes A2 — pour parler de tes souvenirs, habitudes passées, projets futurs et opinions, avec une parole plus fluide et mieux organisée.',
                B1: '20 thèmes B1 — pour construire un discours structuré de 2 à 3 minutes : raconter, argumenter, comparer et nuancer avec assurance.',
                B2: '20 thèmes B2 — pour développer une argumentation claire, structurée et nuancée de 3 à 5 minutes sur l\'économie, le monde professionnel, l\'innovation et les défis sociétaux.',
              }[level]}
            </p>
            <div className="flex justify-center mt-4">
              <div className="flex" data-testid="level-tabs">
                {LEVELS.map((lv, idx) => {
                  const isActive = level === lv;
                  const isFirst = idx === 0;
                  const isLast = idx === LEVELS.length - 1;
                  return (
                    <button
                      key={lv}
                      onClick={() => setLevel(lv)}
                      className={`text-[10px] font-bold tracking-wider px-3 py-1 transition-colors border ${
                        isActive
                          ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                          : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'
                      } ${isFirst ? 'rounded-l-full' : ''} ${isLast ? 'rounded-r-full' : ''} ${!isFirst ? '-ml-px' : ''}`}
                      data-testid={`level-${lv}`}
                    >
                      {lv}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timer mode */}
      <section className="pb-2">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="bg-white border border-[#E8E4DC] rounded-2xl px-4 py-3" data-testid="timer-controls">
            <p className="text-[10px] text-[#6B4E37] uppercase tracking-[0.2em] font-bold mb-1.5">Mode du chrono</p>
            <div className="grid grid-cols-3 gap-1.5" data-testid="timer-modes">
              {timerModes.map((t) => {
                const isActive = mode === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setMode(t.id)}
                    className={`rounded-full text-[11px] font-bold py-1.5 border transition-colors ${
                      isActive
                        ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                        : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'
                    }`}
                    data-testid={`timer-mode-${t.id}`}
                  >
                    {t.name} <span className="opacity-70 font-medium">· {t.sub}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Active theme detail */}
      <section className="pb-8 pt-4">
        <div className="max-w-[760px] mx-auto px-6">
          <ThemeDetail theme={active} timerSeconds={timerSeconds} level={level} />
        </div>
      </section>

      {/* Theme grid */}
      <section className="pb-20">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] text-[#6B4E37] uppercase tracking-[0.25em] font-bold">Tous les thèmes {level}</p>
            <p className="text-[10px] text-[#999]">{themes.length} thèmes</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="theme-grid">
            {themes.map((t, idx) => {
              const isActive = t.id === activeId;
              return (
                <button
                  key={t.id}
                  onClick={() => goToTheme(t.id)}
                  className={`group rounded-xl px-3 py-3 text-left transition-all border ${
                    isActive
                      ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                      : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37] hover:shadow-sm'
                  }`}
                  data-testid={`theme-tile-${t.id}`}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-xl flex-shrink-0" aria-hidden="true">{t.emoji}</span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-[9px] uppercase tracking-wider font-bold ${isActive ? 'text-[#D4AF37]' : 'text-[#999]'}`}>
                        Thème {idx + 1}
                      </p>
                      <p className="text-[11px] font-medium leading-tight mt-0.5 truncate">{t.title}</p>
                    </div>
                    {isActive && <Play className="w-3 h-3 text-[#D4AF37] mt-0.5 flex-shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Progression note */}
          <div className="mt-6 bg-white border border-[#E8E4DC] rounded-2xl px-5 py-4" data-testid="progression-note">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-2">Progression {level}</p>
            <ul className="space-y-1.5 text-[12px] text-[#1A2F4A]/90 leading-relaxed">
              {PROGRESSION_BY_LEVEL[level].map((row, i) => (
                <li key={i} data-testid={`progression-row-${i}`}>
                  <strong>{row.range} :</strong> {row.label}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-[#6B4E37] italic mt-3 pt-3 border-t border-[#F3F4F6]" data-testid="progression-goal">
              {FINAL_GOAL_BY_LEVEL[level]}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
